import { IDataListColumn, Id, useFormatDate, useFormatDateTime } from "@dgs/core";
import { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { DataFieldIndexResource, DataFieldType } from "../../../shared/types/dataField";
import { Address } from "../../../shared/ui/Address";
import { ContactAnchor, ContactMeans } from "../../../shared/ui/ContactMeans";
import { useDataFieldsQuery } from "../shared/api/dataFieldService";
import {
	ContactAttribute,
	ContactAttributeResourceColumn,
	ContactAttributeResourceDataField,
	ContactAttributeResourceRelation,
	ContactColumnAttribute,
	ContactDataFieldAttribute,
	ContactRelationAttribute,
	ContactViewIndexResource,
	IContactAttributeResource,
	IContactIndexResource,
} from "../shared/types";
import { ContactAddressList } from "./ContactAddressList";

const largeFields: DataFieldType[] = [
	DataFieldType.TEXT,
	DataFieldType.CHECKBOX_GROUP,
	DataFieldType.MULTI_SELECT_FIELD,
	DataFieldType.NUMBER,
	DataFieldType.URL,
];

export const getContactListColumnSizes = (
	dataField: DataFieldIndexResource,
): "1fr" | "2fr" | "3fr" | "4fr" | "5fr" | "max-content" | undefined => {
	if (dataField.type === "longText") {
		return "2fr";
	}
	if (largeFields.includes(dataField.type)) {
		return "1fr";
	}
	return "max-content";
};

export function findContactAttribute<T extends ContactAttribute>(id: Id) {
	return function (contactAttribute: ContactAttribute): contactAttribute is T {
		return `${id}` === `${contactAttribute.id}`;
	};
}

const emptyValue = "-";

export const resolveRelationAttributeValue = (relationAttribute?: ContactRelationAttribute) => {
	if (!relationAttribute) {
		return emptyValue;
	}
	let entry;
	if (relationAttribute.value) {
		switch (relationAttribute.entityType) {
			case "companies":
			case "nonPrimaryCompanies":
			case "tenants":
			case "tags":
			case "invitationLists":
			case "distributionLists":
				entry = relationAttribute.value.map((value) => value.name).join(", ");
				break;
			case "emails":
			case "nonPrimaryEmails":
				entry = (
					<ContactMeans
						items={relationAttribute.value.map((email) => email.email)}
						render={(str) => <ContactAnchor type="mailto">{str}</ContactAnchor>}
					/>
				);
				break;
			case "phones":
			case "nonPrimaryPhones":
				entry = (
					<ContactMeans
						items={relationAttribute.value.map((phone) => phone.phoneNumber)}
						render={(str) => <ContactAnchor type="tel">{str}</ContactAnchor>}
					/>
				);
				break;
			case "addresses":
			case "nonPrimaryAddresses":
				entry = <ContactAddressList addresses={relationAttribute.value} />;
				break;
			case "primaryAddress":
				entry = <Address address={relationAttribute.value} />;
				break;
			case "primaryPhone":
				entry = <ContactAnchor type="tel">{relationAttribute.value.phoneNumber}</ContactAnchor>;
				break;
			case "primaryEmail":
				entry = <ContactAnchor type="mailto">{relationAttribute.value.email}</ContactAnchor>;
				break;
			case "primaryCompany":
				entry = relationAttribute.value.name;
				break;
			case "contactType":
				entry = relationAttribute.value.name;
				break;
		}
	}
	return entry || emptyValue;
};

export const useContactListUtils = (currentContactView: ContactViewIndexResource | null) => {
	const { t } = useTranslation();
	const { data } = useDataFieldsQuery();
	const formatDateTime = useFormatDateTime();
	const formatDate = useFormatDate();

	const resolveDataFieldValue = useCallback(
		(value: any, dataField: DataFieldIndexResource) => {
			switch (dataField?.type) {
				case "checkbox":
					return value ? t("Yes") : t("No");
				case "selectField":
				case "radioField":
					return dataField.options.find((option) => `${option.id}` === `${value}`)?.value;
				case "dateTime":
					return formatDateTime(value);
				case "date":
					return formatDate(value);
				case "checkboxGroup":
				case "multiSelectField": {
					const options =
						dataField.options && value ? dataField.options.filter((option) => value.includes(option.id)) : [];
					return options.map((option) => option.value).join(", ");
				}
				case "phone":
					return <ContactAnchor type="tel">{value}</ContactAnchor>;
				case "email":
					return <ContactAnchor type="mailto">{value}</ContactAnchor>;
				default:
					return value;
			}
		},
		[formatDate, formatDateTime, t],
	);

	const resolveColumnValue = useCallback(
		(attribute: ContactAttribute) => {
			switch (attribute.entityType) {
				case "createdAt":
				case "updatedAt":
					return formatDateTime(attribute.value);
				default:
					return attribute.value ? attribute.value : emptyValue;
			}
		},
		[formatDateTime],
	);

	const mapContactColumnAttributeToColumn = useCallback(
		(attribute: ContactAttributeResourceColumn): IDataListColumn<IContactIndexResource> => {
			return {
				heading: t(attribute.entityType),
				size: "1fr",
				valueKey: attribute.id as any,
				type: "value",
				sortable: true,
				render: (contact) => {
					const current = contact.attributes.find(findContactAttribute<ContactColumnAttribute>(attribute.id));

					if (!current) {
						return emptyValue;
					}

					return resolveColumnValue(current);
				},
			};
		},
		[resolveColumnValue, t],
	);

	const mapContactDataFieldAttributeToColumn = useCallback(
		(attribute: ContactAttributeResourceDataField): IDataListColumn<IContactIndexResource> => {
			const dataField = data.find((d) => attribute.entityType === d.id);
			const sortable = dataField?.type !== "multiSelectField" && dataField?.type !== "checkboxGroup";

			return {
				heading: t(dataField?.label as any),
				size: dataField ? getContactListColumnSizes(dataField) : "1fr",
				valueKey: attribute.id as any,
				type: "value",
				sortable: sortable,
				render: (guest) => {
					const current = guest.attributes.find(findContactAttribute<ContactDataFieldAttribute>(attribute.id));

					if (!current || !dataField || !current.value) {
						return emptyValue;
					}

					return resolveDataFieldValue(current.value, dataField) || emptyValue;
				},
			};
		},
		[data, resolveDataFieldValue, t],
	);

	const mapContactRelationAttributeToColumn = useCallback(
		(attribute: ContactAttributeResourceRelation): IDataListColumn<IContactIndexResource> => {
			const render = (contact: IContactIndexResource) =>
				resolveRelationAttributeValue(
					contact.attributes.find(findContactAttribute<ContactRelationAttribute>(attribute.id)),
				);
			const sortable = ["primaryCompany", "primaryPhone", "primaryEmail", "contactType"].includes(attribute.entityType);
			return {
				heading: t(attribute.entityType),
				size: "1fr",
				valueKey: attribute.id as any,
				type: "value",
				sortable: sortable,
				render,
			};
		},
		[t],
	);

	const mapContactAttributeToColumn = useCallback(
		(attribute: IContactAttributeResource) => {
			switch (attribute.type) {
				case "column":
					return mapContactColumnAttributeToColumn(attribute);
				case "dataField":
					return mapContactDataFieldAttributeToColumn(attribute);
				case "relation":
					return mapContactRelationAttributeToColumn(attribute);
			}
		},
		[mapContactColumnAttributeToColumn, mapContactDataFieldAttributeToColumn, mapContactRelationAttributeToColumn],
	);

	const resolveAttributeValue = useCallback(
		(attribute: ContactAttribute) => {
			switch (attribute.type) {
				case "column":
					return resolveColumnValue(attribute);
				case "dataField": {
					const dataField = data.find((d) => attribute.entityType === d.id);
					return dataField ? resolveDataFieldValue(attribute.value, dataField) : emptyValue;
				}
				case "relation":
					return resolveRelationAttributeValue(attribute);
			}
		},
		[data, resolveColumnValue, resolveDataFieldValue],
	);

	const columns = useMemo(() => {
		const cols = currentContactView ? currentContactView.contactAttributes.map(mapContactAttributeToColumn) : [];
		return cols.every((c) => c.size === "max-content")
			? cols.map((c, index) => {
					if (index === 0) {
						return {
							...c,
							size: "1fr" as const,
						};
					}
					return c;
				})
			: cols;
	}, [currentContactView, mapContactAttributeToColumn]);

	return {
		columns,
		mapContactAttributeToColumn,
		resolveAttributeValue,
	};
};
